import {
  ControlValueAccessor,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
} from '@angular/forms'
import { distinctUntilChanged } from 'rxjs/operators'

const noop = (): void => {} // eslint-disable-line

/**
 * Factory for providers to mark the component as a ng_value_accessor
 * use like this :
 * ```ts
 * @Component({
 *  providers:[CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR(() => ComponnentClass)]
 * })
 * ```
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = function <T>(comp: Type<T>) {
  return {
    provide: NG_VALUE_ACCESSOR,
    useExisting: comp,
    multi: true
  };
}

 */

export class CustomNestedForm implements Validator, ControlValueAccessor {
  public form: UntypedFormGroup

  /**  Placeholders for the callbacks which are later provided
   * by the Control Value Accessor **/
  protected _onTouchedCallback: () => void = noop

  /**  Placeholders for the callbacks which are later provided
   * by the Control Value Accessor **/
  protected _onChangedCallback: () => void = noop

  /**  Set touched on blur **/
  public onBlur(): void {
    this._onTouchedCallback()
  }

  /**  Set touched on blur **/
  public onChange(): void {
    this._onChangedCallback()
  }

  /** Writes the value in the form
   * @param { any } value the value typed
   */
  public writeValue(value: any): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false })
      this._onTouchedCallback()
    }
  }

  /** Register the change of the form */
  public registerOnChange(fn: any): void {
    this._onChangedCallback = fn
  }

  /** Register the touch callback */
  public registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn
  }

  /** Register the changes of validator */
  public registerOnValidatorChange(fn: () => void): void {
    this.form?.statusChanges
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(fn)
  }

  /** Sets the disabled state to the form
   * @param { boolean } isDisabled whether or not the form is disabled
   */
  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable()
  }

  /** Validates the form and returns an error if unvalid
   * @return { ValidationErrors | null }
   */
  public validate(): ValidationErrors | null {
    return this.form.valid
      ? null
      : { customNestedFormControlError: 'Error in custom nested form' }
  }
}
